<template>
    <v-row>
        <!-- COACHES -->
        <v-col v-if="isCompany" cols="12">
            <kimsa-combobox :label="$t('common.coach')" :placeholder="$t('common.select_placeholder')" dense solo
                :items="coaches" @change="setCurrentCoach" :value="currentCoach" placeholder-class item-value="objectID"
                searchable />
        </v-col>
        <!-- GROUP BY -->
        <v-col cols="12">
            <select-option-students @change="currentGroupBy = $event" no-list />
        </v-col>
        <!-- STUDENT FILTER -->
        <v-col cols="12" v-if="currentGroupBy === 'student'">
            <kimsa-combobox :label="$tc('student.student', 1)" :placeholder="$t('common.select_placeholder')" dense solo
                :items="students" @change="setCurrentStudent" :value="currentStudent" placeholder-class
                item-value="objectID" multipleWithChips searchable :readonly="isCompany && !currentCoach" />
        </v-col>
        <!-- GROUP FILTER -->
        <v-col cols="12" v-if="currentGroupBy === 'group'">
            <kimsa-combobox :label="$t('common.group')" :placeholder="$t('common.select_placeholder')" dense solo
                :items="groups" @change="setCurrentGroup" :value="currentGroup" placeholder-class item-value="objectID"
                searchable />
        </v-col>
        <!-- TEAM FILTER -->
        <template v-if="currentGroupBy === 'team'">
            <v-col cols="12" md="6">
                <kimsa-combobox :label="$t('common.team')" :placeholder="$t('common.select_placeholder')" dense solo
                    :items="teams" @change="setCurrentTeam" :value="currentTeam" placeholder-class item-value="objectID"
                    searchable />
            </v-col>
            <v-col cols="12" md="6">
                <kimsa-combobox :label="$t('common.group')" :placeholder="$t('common.select_placeholder')" dense solo
                    :items="teamGroups" @change="setCurrentTeamGroup" :readonly="!currentTeam" :value="currentTeamGroup"
                    placeholder-class item-value="objectID" searchable />
            </v-col>
        </template>
    </v-row>
</template>

<script>
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { UserIndex } from "@/utils/Algolia";
import { mapActions, mapState } from "vuex";

export default {
    name: "SetSelectionRoutine",
    components: { SelectOptionStudents, KimsaCombobox },
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            coaches: [],
            currentCoach: null,
            currentGroupBy: null,
            students: [],
            currentStudent: null,
            currentGroup: null,
            currentTeam: null,
            currentTeamGroup: null,
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        teamGroups(vm = this) {
            return vm.currentTeam?.groups.map(group => {
                return {
                    teamId: vm.currentTeam.id,
                    ...group,
                    id: group.id,
                }
            }) || []
        },
        currentSelection(vm = this) {
            switch (vm.currentGroupBy) {
                case "student":
                    return vm.currentStudent
                case "group":
                    return vm.currentGroup
                case "team":
                    return vm.currentTeamGroup
                default:
                    return null
            }
        },
        collectionName(vm = this) {
            return vm.currentGroupBy === 'student' ? 'assignedRoutines' : 'groupAssignedRoutines'
        },
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        },
        currentCoachId(vm = this) {
            return vm.currentCoach?.objectID || vm.coachId
        },
    },
    async mounted() {
        if (this.isCompany) await this.loadCoaches()
        await this.setDefaults()
    },
    methods: {
        ...mapActions(['setLibraryCoach']),
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.companyId} AND coachSubscription.paused != 1`
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 1000,
            })
                .then(({ hits }) => {
                    vm.coaches = hits.map(hit => {
                        return {
                            ...hit,
                            name: hit.name || hit.email,
                        }
                    })
                })
                .catch(err => {
                    console.log(err);
                });
        },
        async setDefaults() {
            const vm = this
            await vm.loadStudents()
        },
        async loadStudents() {
            const vm = this
            if (!vm.currentCoachId) return
            let filter = `studentSubscription.coachId: ${vm.currentCoachId} AND studentSubscription.paused != 1`
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 1000,
            })
                .then(({ hits }) => {
                    vm.students = hits
                })
                .catch(err => {
                    console.log(err);
                });
        },
        setCurrentCoach(coach) {
            const vm = this
            if (coach && typeof coach === 'object') {
                vm.currentCoach = coach
            } else if (coach) {
                let foundCoach = vm.coaches?.find(co => co.name?.toLowerCase() === coach.toLowerCase()) || null
                foundCoach ? vm.currentCoach = foundCoach : vm.currentCoach = null
            } else {
                vm.currentCoach = null
            }
            if (vm.isCompany) vm.setLibraryCoach(vm.currentCoach)
            vm.setDefaults()
        },
        setCurrentStudent(student) {
            const vm = this
            if (!student) return;
            if (typeof student[student.length - 1] === 'string') student.pop()
            vm.currentStudent = student
        },
        setCurrentGroup(group) {
            const vm = this
            if (group && typeof group === 'object') {
                vm.currentGroup = group
            } else if (group) {
                let foundGroup = vm.groups?.find(gr => gr.name?.toLowerCase() === group.toLowerCase()) || null
                foundGroup ? vm.currentGroup = foundGroup : vm.currentGroup = null
            } else {
                vm.currentGroup = null
            }
        },
        setCurrentTeam(team) {
            const vm = this
            if (team && typeof team === 'object') {
                vm.currentTeam = team
            } else if (team) {
                let foundTeam = vm.teams?.find(tm => tm.name?.toLowerCase() === team.toLowerCase()) || null
                foundTeam ? vm.currentTeam = foundTeam : vm.currentTeam = null
            } else {
                vm.currentTeam = null
            }
        },
        setCurrentTeamGroup(teamGroup) {
            const vm = this
            if (teamGroup && typeof teamGroup === 'object') {
                vm.currentTeamGroup = teamGroup
            } else if (teamGroup) {
                let foundTeamGroup = vm.teamGroups?.find(tmg => tmg.name?.toLowerCase() === teamGroup.toLowerCase()) || null
                foundTeamGroup ? vm.currentTeamGroup = foundTeamGroup : vm.currentTeamGroup = null
            } else {
                vm.currentTeamGroup = null
            }
        },
        reset() {
            const vm = this;
            vm.currentStudent = null
            vm.currentGroup = null
            vm.currentTeam = null
            vm.currentTeamGroup = null
        }
    },
    watch: {
        currentCoach() {
            this.currentStudent = null
            this.currentGroup = null
            this.currentTeam = null
            this.currentTeamGroup = null
        },
        currentGroupBy(val) {
            if (val === 'student') {
                this.currentGroup = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'group') {
                this.currentStudent = null
                this.currentTeam = null
                this.currentTeamGroup = null
            }
            if (val === 'team') {
                this.currentStudent = null
                this.currentGroup = null
            }
        },
        currentSelection(val) {
            this.$emit('change', val)
        },
        collectionName(val) {
            this.$emit('collection', val)
        },
        coachId() {
            this.setCurrentCoach()
        }
    }
}
</script>

<style scoped></style>